import type { DataTableFilterField } from '@/components/data-table/types'
import { field, createSchema } from '@/lib/store/schema'
import type { FacilityOption } from '@/hooks/useFacilityOptions'
import type { ProviderOption } from '@/hooks/useProviderOptions'
import type { AppointmentRecord } from '../types'

/**
 * The toolbar's Slots toggle — whether open availability slots render
 * alongside appointments. Not a filter field (never sent to the appointments
 * query), but kept in the URL like the filters so a shared link carries it
 * (`appointments.show_open_slots=true`). Unset means the view's default —
 * hidden in Table, shown in Calendar (see resolveShowOpenSlots).
 */
export const SHOW_OPEN_SLOTS_FIELD = 'show_open_slots'

type OptionsState = {
  state?: 'loading' | 'error' | 'ready'
  onRetry?: () => void
}

/**
 * OpenStatus filter fields for the Appointments v3 table. Scoped to the VISIBLE
 * columns only; start_time is intentionally not filterable (time-of-day
 * filtering is low value). Used by DataTableFilterControls (sidebar) and
 * the filter sidebar.
 *
 * Provider/Location are compact multi-select pickers (mirrors Call Logs'
 * `duty_id` filter) rather than free-text `contains` — they filter
 * appointments by `provider_id`/`facility_id`, not the unreliable
 * `provider_name`/`location` text columns (relay dropped those filters; see
 * the companion relay change).
 */
export function createAppointmentsFilterFields(
  providers: ProviderOption[] = [],
  providerOptionsState: OptionsState = {},
  facilities: FacilityOption[] = [],
  facilityOptionsState: OptionsState = {}
): DataTableFilterField<AppointmentRecord>[] {
  return [
    {
      // Drives both the appointments table/calendar query
      // (source_appointment_date) and the calendar's open-availability query
      // (start_time) — "Date", not "Appointment Date", since it now scopes
      // more than just appointments.
      label: 'Date',
      value: 'source_appointment_date',
      type: 'timerange',
      defaultOpen: true,
    },
    {
      label: 'Type',
      value: 'appointment_type',
      type: 'input',
      defaultOpen: true,
    },
    {
      label: 'Location',
      value: 'facility_id',
      type: 'checkbox',
      defaultOpen: true,
      compact: true,
      // Facility population is uneven across orgs, so Location combines the
      // real facility select with free text: typing a value not in the list
      // adds it as a literal filter chip alongside picked facilities (relay's
      // facility_id filter falls back to a `location`/facility-name `contains`
      // match for values that don't coerce to a uuid).
      creatable: true,
      optionsState: facilityOptionsState.state,
      onOptionsRetry: facilityOptionsState.onRetry,
      options: facilities.map((facility) => ({
        label: facility.name,
        value: facility.id,
      })),
    },
    {
      label: 'Status',
      value: 'status',
      type: 'input',
      defaultOpen: true,
    },
    {
      label: 'Provider',
      value: 'provider_id',
      type: 'checkbox',
      defaultOpen: true,
      compact: true,
      optionsState: providerOptionsState.state,
      onOptionsRetry: providerOptionsState.onRetry,
      options: providers.map((provider) => ({
        label: provider.name,
        value: provider.id,
      })),
    },
  ]
}

/**
 * BYOS schema for URL serialization.
 * timerange -> array(timestamp); input -> string; checkbox -> array(string).
 */
/**
 * The table id, and the URL param the deep link is addressed by. One
 * definition: the adapter namespaces every param as `${TABLE_ID}.`
 * (`namespaceUrlKeys`), so the copy-link builder in AppointmentsSheetDetails
 * has to agree with it exactly. Spelled by hand in two files, a rename here
 * would break the produced link in production with the suite still green.
 */
export const APPOINTMENTS_TABLE_ID = 'appointments'
export const APPOINTMENT_ID_PARAM = `${APPOINTMENTS_TABLE_ID}.id`

export const appointmentsSchema = createSchema({
  // Deep-link only — NOT in createAppointmentsFilterFields, so it never renders
  // as a filter chip. A shared appointment link carries
  // `?appointments.id=<uuid>&details=1`; AppointmentsList reads THIS half for
  // the initial row selection and an exact-id query filter that narrows the
  // table to that one appointment. `details=1` rides along for parity with the
  // Clients/Call Logs links but is not required to reopen the sheet — it is
  // derived from row selection, so it is stripped from any URL that arrives
  // without a row to select. Cleared when the user dismisses the sheet.
  id: field.string().default(null),
  source_appointment_date: field
    .array(field.timestamp())
    .delimiter('-')
    .default([]),
  appointment_type: field.string().default(null),
  facility_id: field.array(field.string()).delimiter(',').default([]),
  status: field.string().default(null),
  provider_id: field.array(field.string()).delimiter(',').default([]),
  [SHOW_OPEN_SLOTS_FIELD]: field.boolean().default(null),
})

export type AppointmentsFilterState = typeof appointmentsSchema._type
